import React, { useEffect, useMemo, useState } from 'react';
import { Container, Card, Button, ButtonGroup, Spinner, Alert, Badge, Row, Col } from 'react-bootstrap';
import moment from 'moment';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { API_BASE } from '../lib/apiBase';
import { resolveAssetUrl } from '../lib/assetUrl';

/**
 * AdminEvents provides a management view of all events (including pending ones)
 * with quick links to edit or delete existing entries.
 */
const AdminEvents = () => {
  const navigate = useNavigate();
  const [view, setView] = useState('upcoming');
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const role = sessionStorage.getItem('userRole');

  useEffect(() => {
    if (role !== 'admin') {
      setLoading(false);
      return;
    }
    let alive = true;
    (async () => {
      try {
        setLoading(true);
        setError('');
        const { data } = await axios.get(`${API_BASE}/events?scope=manage`, {
          headers: { 'x-user-role': sessionStorage.getItem('userRole') || 'admin' },
        });
        if (!alive) return;
        setEvents(Array.isArray(data) ? data : []);
      } catch (err) {
        if (!alive) return;
        console.error('Failed to load events:', err);
        setError('Unable to load events. Please try again.');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  const today = moment();

  const upcoming = useMemo(
    () =>
      events
        .filter((event) => moment(event.date).isSameOrAfter(today, 'day'))
        .sort((a, b) => moment(a.date).diff(moment(b.date))),
    [events, today]
  );

  const past = useMemo(
    () =>
      events
        .filter((event) => moment(event.date).isBefore(today, 'day'))
        .sort((a, b) => moment(b.date).diff(moment(a.date))),
    [events, today]
  );

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this event?')) return;
    try {
      await axios.delete(`${API_BASE}/events/${id}`, {
        headers: { 'x-user-role': sessionStorage.getItem('userRole') || 'admin' },
      });
      setEvents((prev) => prev.filter((event) => (event._id || event.id) !== id));
    } catch (err) {
      console.error('Failed to delete event:', err);
      alert('Unable to delete the event.');
    }
  };

  const renderBadge = (event) => {
    if (event.status === 'pending') {
      return <Badge bg="warning" className="ms-2">Pending Approval</Badge>;
    }
    if (event.pendingChange?.changeType) {
      return <Badge bg="info" className="ms-2">Update Pending</Badge>;
    }
    return <Badge bg="success" className="ms-2">Published</Badge>;
  };

  const renderEvents = (list) => (
    <Row className="g-4">
      {list.map((event) => {
        const id = event._id || event.id;
        return (
          <Col md={6} key={id}>
            <Card className="h-100 shadow-sm border-0">
              <Card.Header className="bg-white border-0">
                <div className="d-flex justify-content-between align-items-start">
                  <div>
                    <div className="fw-bold" style={{ color: '#2b333d' }}>{event.title}</div>
                    <small className="text-muted">{moment(event.date).format('MMM D, YYYY')}</small>
                    {renderBadge(event)}
                  </div>
                  <div className="d-flex gap-2">
                    <Button size="sm" variant="outline-secondary" onClick={() => navigate(`/a7dash87/edit-event/${id}`)}>
                      Edit
                    </Button>
                    <Button size="sm" variant="outline-danger" onClick={() => handleDelete(id)}>
                      Delete
                    </Button>
                  </div>
                </div>
              </Card.Header>
              {Array.isArray(event.images) && event.images.length > 0 && (
                <div style={{ width: '100%', paddingTop: '56%', position: 'relative', backgroundColor: '#f5f5f5' }}>
                  <img
                    src={resolveAssetUrl(event.images[0])}
                    alt={`${event.title}-cover`}
                    style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                </div>
              )}
              <Card.Body>
                <Card.Text style={{ color: '#2b333d', whiteSpace: 'pre-wrap' }}>{event.description}</Card.Text>
                {event.pendingChange?.data && (
                  <Alert variant="info" className="mt-3 mb-0">
                    This event has an update pending approval.
                  </Alert>
                )}
              </Card.Body>
            </Card>
          </Col>
        );
      })}
      {list.length === 0 && (
        <Col>
          <div className="text-center py-5 text-muted">No events found in this view.</div>
        </Col>
      )}
    </Row>
  );

  if (role !== 'admin') {
    return (
      <Container className="py-5">
        <Alert variant="warning">Only admin users can manage events.</Alert>
      </Container>
    );
  }

  if (loading) {
    return (
      <Container className="py-5 text-center">
        <Spinner animation="border" />
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="py-5">
        <Alert variant="danger">{error}</Alert>
      </Container>
    );
  }

  return (
    <Container className="py-5">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2 className="mb-1">Manage Events</h2>
          <div className="text-muted">Review all events, including pending submissions from the office team.</div>
        </div>
        <Button variant="primary" onClick={() => navigate('/a7dash87/create-event')}>
          Create Event
        </Button>
      </div>

      <ButtonGroup className="mb-4">
        <Button variant={view === 'upcoming' ? 'primary' : 'outline-primary'} onClick={() => setView('upcoming')}>
          Upcoming Events
        </Button>
        <Button variant={view === 'past' ? 'primary' : 'outline-primary'} onClick={() => setView('past')}>
          Past Events
        </Button>
      </ButtonGroup>

      {view === 'upcoming' ? renderEvents(upcoming) : renderEvents(past)}
    </Container>
  );
};

export default AdminEvents;
